import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import config from '../../config';
import { useAppContext } from '../AppContext';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  Tooltip,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Grid,
  Stack,
  Divider
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LabelIcon from '@mui/icons-material/Label';

const TIPUS_OPTIONS = ['DESPESA', 'INGRES', 'INVERSIO'];

const getTipusColor = (tipus) => {
  switch (tipus) {
    case 'INGRES':
      return 'success';
    case 'DESPESA':
      return 'error';
    case 'INVERSIO':
      return 'info';
    default:
      return 'default';
  }
};

const extractErrorMessage = (err, fallback) => {
  if (err.response?.status === 403) {
    return "Accés denegat (403): Cal el rol ROLE_ADMIN. Fes Logout i Login amb Google per actualitzar el token.";
  }
  return (
    err.response?.data?.detail ||
    err.response?.data?.message ||
    (typeof err.response?.data === 'string' ? err.response.data : null) ||
    fallback
  );
};

const ConceptesAdmin = ({ userapp, showNotification }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { ctxUser } = useAppContext();
  const jwtToken = ctxUser?.fdvToken?.jwtToken || '';

  const [conceptes, setConceptes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTipus, setFilterTipus] = useState('ALL');

  // Modal Crear / Editar
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null); // null = crear, object = editar
  const [formData, setFormData] = useState({ nom: '', tipus: 'DESPESA', agrupacio: '' });
  const [saving, setSaving] = useState(false);

  // Modal Canviar Nom (Migrar)
  const [openRenameModal, setOpenRenameModal] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [impactCount, setImpactCount] = useState(null);
  const [impactLoading, setImpactLoading] = useState(false);
  const [newConceptName, setNewConceptName] = useState('');
  const [renaming, setRenaming] = useState(false);

  // Modal Eliminar / Eliminar amb reassignació
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteImpactCount, setDeleteImpactCount] = useState(null);
  const [deleteImpactLoading, setDeleteImpactLoading] = useState(false);
  const [destinationConcept, setDestinationConcept] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Carregar llista de conceptes
  const fetchConceptes = useCallback(async () => {
    if (!jwtToken || !userapp) return;
    setLoading(true);
    try {
      const res = await axios.get(`${config.apiBaseUrl}/v1/conceptes/byuserapp?userapp=${userapp}`, {
        headers: { Authorization: jwtToken }
      });
      const data = res.data || [];
      const sorted = [...data].sort((a, b) => a.nom.localeCompare(b.nom));
      setConceptes(sorted);
    } catch (err) {
      console.error('Error al carregar conceptes:', err);
      showNotification('Error al carregar el catàleg de conceptes', 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwtToken, userapp]);

  useEffect(() => {
    fetchConceptes();
  }, [fetchConceptes]);

  // Filtrar conceptes per cerca i tipus
  const filteredConceptes = useMemo(() => {
    return conceptes.filter((c) => {
      const matchesSearch = c.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.agrupacio && c.agrupacio.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesTipus = filterTipus === 'ALL' || c.tipus === filterTipus;
      return matchesSearch && matchesTipus;
    });
  }, [conceptes, searchTerm, filterTipus]);


  // Obrir modal de Creació
  const handleOpenCreate = () => {
    setEditingItem(null);
    setFormData({ nom: '', tipus: 'DESPESA', agrupacio: '' });
    setOpenEditModal(true);
  };

  // Obrir modal d'Edició estàndard (nom bloquejat)
  const handleOpenEdit = (item) => {
    setEditingItem(item);
    setFormData({
      nom: item.nom,
      tipus: item.tipus || 'DESPESA',
      agrupacio: item.agrupacio || ''
    });
    setOpenEditModal(true);
  };

  // Desar Creació o Edició
  const handleSaveConcept = async () => {
    if (!formData.nom.trim()) {
      showNotification('El nom del concepte és obligatori', 'warning');
      return;
    }
    setSaving(true);
    try {
      if (editingItem) {
        // PUT /api/v1/conceptes/{id} - nom no es pot canviar
        const payload = {
          id: editingItem.id,
          userApp: userapp,
          nom: editingItem.nom, // mantenir nom original
          tipus: formData.tipus,
          agrupacio: formData.agrupacio.trim()
        };
        await axios.put(`${config.apiBaseUrl}/v1/conceptes/${editingItem.id}`, payload, {
          headers: { Authorization: jwtToken }
        });
        showNotification('Concepte actualitzat correctament', 'success');
      } else {
        // POST /api/v1/conceptes/
        const payload = {
          userApp: userapp,
          nom: formData.nom.trim(),
          tipus: formData.tipus,
          agrupacio: formData.agrupacio.trim()
        };
        await axios.post(`${config.apiBaseUrl}/v1/conceptes/`, payload, {
          headers: { Authorization: jwtToken }
        });
        showNotification('Nou concepte creat correctament', 'success');
      }
      setOpenEditModal(false);
      fetchConceptes();
    } catch (err) {
      console.error('Error en desar concepte:', err);
      const msg = extractErrorMessage(err, 'Error en desar el concepte');
      showNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Obrir diàleg de Renombrat amb càlcul d'impacte
  const handleOpenRename = async (item) => {
    setRenameTarget(item);
    setNewConceptName('');
    setImpactCount(null);
    setOpenRenameModal(true);
    setImpactLoading(true);
    try {
      const res = await axios.get(`${config.apiBaseUrl}/v1/conceptes/${item.id}/impact`, {
        headers: { Authorization: jwtToken }
      });
      setImpactCount(res.data?.numMoviments ?? 0);
    } catch (err) {
      console.error('Error al consultar impacte:', err);
      showNotification('No s’ha pogut calcular l’impacte dels moviments', 'warning');
      setImpactCount(0);
    } finally {
      setImpactLoading(false);
    }
  };

  // Executar Renombrat massiu
  const handleExecuteRename = async () => {
    const trimmed = newConceptName.trim();
    if (!trimmed) {
      showNotification('El nou nom no pot estar buit', 'warning');
      return;
    }
    if (trimmed.toUpperCase() === renameTarget.nom.toUpperCase()) {
      showNotification('El nou nom ha de ser diferent de l’actual', 'warning');
      return;
    }
    setRenaming(true);
    try {
      const res = await axios.post(
        `${config.apiBaseUrl}/v1/conceptes/${renameTarget.id}/rename`,
        { nouNom: trimmed },
        { headers: { Authorization: jwtToken } }
      );
      const updatedMovs = res.data?.movimentsActualitzats ?? impactCount ?? 0;
      showNotification(`Concepte renombrat correctament. S’han actualitzat ${updatedMovs} moviments.`, 'success');
      setOpenRenameModal(false);
      fetchConceptes();
    } catch (err) {
      console.error('Error en renombrar concepte:', err);
      const msg = extractErrorMessage(err, 'Error en renombrar el concepte');
      showNotification(msg, 'error');
    } finally {
      setRenaming(false);
    }
  };

  // Obrir diàleg d'Eliminació amb consulta d'impacte
  const handleOpenDelete = async (item) => {
    setDeleteTarget(item);
    setDeleteImpactCount(null);
    setDestinationConcept('');
    setOpenDeleteModal(true);
    setDeleteImpactLoading(true);
    try {
      const res = await axios.get(`${config.apiBaseUrl}/v1/conceptes/${item.id}/impact`, {
        headers: { Authorization: jwtToken }
      });
      setDeleteImpactCount(res.data?.numMoviments ?? 0);
    } catch (err) {
      console.error('Error al consultar impacte per esborrar:', err);
      showNotification('No s’ha pogut verificar l’impacte de moviments', 'warning');
      setDeleteImpactCount(0);
    } finally {
      setDeleteImpactLoading(false);
    }
  };

  // Executar Eliminació (directa o amb reassignació)
  const handleExecuteDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (deleteImpactCount > 0) {
        if (!destinationConcept) {
          showNotification('Has de seleccionar un concepte de destinació', 'warning');
          setDeleting(false);
          return;
        }
        // POST /api/v1/conceptes/{id}/delete-and-migrate
        const res = await axios.post(
          `${config.apiBaseUrl}/v1/conceptes/${deleteTarget.id}/delete-and-migrate`,
          { concepteDesti: destinationConcept },
          { headers: { Authorization: jwtToken } }
        );
        const reassignats = res.data?.movimentsReassignats ?? deleteImpactCount;
        showNotification(`Concepte eliminat i ${reassignats} moviments reassignats a "${destinationConcept}".`, 'success');
      } else {
        // DELETE directe /api/v1/conceptes/{id}
        await axios.delete(`${config.apiBaseUrl}/v1/conceptes/${deleteTarget.id}`, {
          headers: { Authorization: jwtToken }
        });
        showNotification(`Concepte "${deleteTarget.nom}" eliminat correctament.`, 'success');
      }
      setOpenDeleteModal(false);
      fetchConceptes();
    } catch (err) {
      console.error('Error en eliminar concepte:', err);
      const msg = extractErrorMessage(err, 'Error en eliminar el concepte');
      showNotification(msg, 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      {/* Barra superior de controls i filtres */}
      <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={5} md={5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Cercar concepte o agrupació..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={7} sm={4} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipus</InputLabel>
              <Select
                value={filterTipus}
                label="Tipus"
                onChange={(e) => setFilterTipus(e.target.value)}
              >
                <MenuItem value="ALL">Tots els tipus</MenuItem>
                {TIPUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={5} sm={3} md={3} sx={{ textAlign: 'right' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
              fullWidth={isMobile}
              sx={{ textTransform: 'none', fontWeight: 'bold' }}
            >
              Nou Concepte
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Indicador de càrrega */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredConceptes.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          No s’ha trobat cap concepte que coincideixi amb la cerca per al compte <strong>{userapp}</strong>.
        </Alert>
      ) : isMobile ? (
        /* VISTA MÒBIL: LLISTA DE TARGETES (PROHIBIT TAULES HORIZONTALS) */
        <Stack spacing={1.5}>
          {filteredConceptes.map((c) => (
            <Card key={c.id} variant="outlined" sx={{ borderRadius: 2, boxShadow: 1 }}>
              <CardContent sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {c.nom}
                  </Typography>
                  <Chip
                    label={c.tipus || 'SENSE TIPUS'}
                    color={getTipusColor(c.tipus)}
                    size="small"
                    sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  <strong>Agrupació:</strong> {c.agrupacio || '—'}
                </Typography>
              </CardContent>
              <Divider />
              <CardActions sx={{ justifyContent: 'flex-end', px: 1, py: 0.5 }}>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => handleOpenEdit(c)}
                  sx={{ textTransform: 'none' }}
                >
                  Editar
                </Button>
                <Button
                  size="small"
                  color="warning"
                  startIcon={<DriveFileRenameOutlineIcon />}
                  onClick={() => handleOpenRename(c)}
                  sx={{ textTransform: 'none' }}
                >
                  Renombrar
                </Button>
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => handleOpenDelete(c)}
                  sx={{ textTransform: 'none' }}
                >
                  Eliminar
                </Button>
              </CardActions>
            </Card>
          ))}
        </Stack>
      ) : (
        /* VISTA ESCRIPTORI: TAULA COMPLETA */
        <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: 'action.hover' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Nom del Concepte</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Tipus</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Agrupació</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Accions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredConceptes.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{c.nom}</TableCell>
                  <TableCell>
                    <Chip
                      label={c.tipus || 'SENSE TIPUS'}
                      color={getTipusColor(c.tipus)}
                      size="small"
                      sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}
                    />
                  </TableCell>
                  <TableCell>{c.agrupacio || '—'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar tipus i agrupació">
                      <IconButton size="small" color="primary" onClick={() => handleOpenEdit(c)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Canviar nom i migrar moviments">
                      <IconButton size="small" color="warning" onClick={() => handleOpenRename(c)}>
                        <DriveFileRenameOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar concepte">
                      <IconButton size="small" color="error" onClick={() => handleOpenDelete(c)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* DIÀLEG 1: CREAR O EDITAR CONCEPTE */}
      <Dialog
        open={openEditModal}
        onClose={() => !saving && setOpenEditModal(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <LabelIcon color="primary" />
          {editingItem ? 'Editar Concepte' : 'Nou Concepte'}
        </DialogTitle>
        <DialogContent dividers>
          {editingItem && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Per seguretat d’integritat referencial, el camp <strong>Nom</strong> no es pot alterar directament.
              Si vols reanomenar el concepte, fes servir el botó <strong>"Canviar Nom / Migrar"</strong>.
            </Alert>
          )}

          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label="Nom del Concepte"
              fullWidth
              value={formData.nom}
              onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
              disabled={Boolean(editingItem)}
              required
              helperText={
                editingItem
                  ? 'Nom bloquejat per a preservar la integritat dels moviments.'
                  : 'Identificador literal del concepte.'
              }
            />

            <FormControl fullWidth>
              <InputLabel>Tipus de Concepte</InputLabel>
              <Select
                value={formData.tipus}
                label="Tipus de Concepte"
                onChange={(e) => setFormData({ ...formData, tipus: e.target.value })}
              >
                {TIPUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Agrupació"
              fullWidth
              value={formData.agrupacio}
              onChange={(e) => setFormData({ ...formData, agrupacio: e.target.value })}
              placeholder="Ex: LLOGUER I SUBMINISTRAMENTS, ALIMENTACIO..."
              helperText="Categoria o agrupació per a informes i estadístiques"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenEditModal(false)} disabled={saving} color="inherit">
            Cancel·lar
          </Button>
          <Button
            onClick={handleSaveConcept}
            variant="contained"
            color="primary"
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIÀLEG 2: CANVIAR NOM I MIGRAR MOVIMENTS HISTÒRICS */}
      <Dialog
        open={openRenameModal}
        onClose={() => !renaming && setOpenRenameModal(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <DriveFileRenameOutlineIcon color="warning" />
          Canviar Nom del Concepte (Migració Massiva)
        </DialogTitle>
        <DialogContent dividers>
          {impactLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3, gap: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2">Consultant impacte en moviments bancaris...</Typography>
            </Box>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="warning" icon={<WarningAmberIcon fontSize="inherit" />}>
                <Typography variant="body2">
                  Atenció: El concepte <strong>"{renameTarget?.nom}"</strong> s’utilitza actualment en{' '}
                  <strong>{impactCount ?? 0} moviments bancaris</strong>.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  En confirmar, el concepte serà reanomenat i <strong>tots els moviments històrics s’actualitzaran automàticament</strong> en una única transacció atòmica.
                </Typography>
              </Alert>

              <TextField
                label="Nom Actual"
                fullWidth
                value={renameTarget?.nom || ''}
                disabled
              />

              <TextField
                label="Nou Nom del Concepte"
                fullWidth
                required
                autoFocus
                value={newConceptName}
                onChange={(e) => setNewConceptName(e.target.value)}
                placeholder="Introdueix el nou nom literal..."
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenRenameModal(false)} disabled={renaming} color="inherit">
            Cancel·lar
          </Button>
          <Button
            onClick={handleExecuteRename}
            variant="contained"
            color="warning"
            disabled={renaming || impactLoading || !newConceptName.trim()}
          >
            {renaming ? <CircularProgress size={24} /> : 'Confirmar i Renombrar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIÀLEG 3: ELIMINACIÓ AMB REASSIGNACIÓ OBLIGATÒRIA */}
      <Dialog
        open={openDeleteModal}
        onClose={() => !deleting && setOpenDeleteModal(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          Eliminar Concepte: {deleteTarget?.nom}
        </DialogTitle>
        <DialogContent dividers>
          {deleteImpactLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3, gap: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2">Verificant moviments associats...</Typography>
            </Box>
          ) : deleteImpactCount === 0 ? (
            <Box sx={{ py: 1 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Aquest concepte no té cap moviment bancari associat.
              </Alert>
              <Typography variant="body1">
                Estàs segur que vols eliminar permanentment el concepte <strong>"{deleteTarget?.nom}"</strong>?
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="error">
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  Aquest concepte no es pot esborrar directament perquè té {deleteImpactCount} moviments associats.
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Per no deixar moviments orfes, has de seleccionar un <strong>concepte de destinació</strong>. Tots els moviments seran transferits abans d’esborrar aquest concepte.
                </Typography>
              </Alert>

              <FormControl fullWidth required>
                <InputLabel>Concepte de Destinació</InputLabel>
                <Select
                  value={destinationConcept}
                  label="Concepte de Destinació"
                  onChange={(e) => setDestinationConcept(e.target.value)}
                >
                  {conceptes
                    .filter((c) => c.nom !== deleteTarget?.nom)
                    .map((c) => (
                      <MenuItem key={c.id} value={c.nom}>
                        {c.nom} ({c.tipus})
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDeleteModal(false)} disabled={deleting} color="inherit">
            Cancel·lar
          </Button>
          <Button
            onClick={handleExecuteDelete}
            variant="contained"
            color="error"
            disabled={
              deleting ||
              deleteImpactLoading ||
              (deleteImpactCount > 0 && !destinationConcept)
            }
          >
            {deleting ? <CircularProgress size={24} /> : deleteImpactCount > 0 ? 'Reassignar i Eliminar' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConceptesAdmin;
